import "@vidstack/react/player/styles/base.css";
import "@vidstack/react/player/styles/plyr/theme.css";
import "./styles.scss";

import type {
  MediaPlayerInstance,
  MediaProviderAdapter,
} from "@vidstack/react";
import {
  isHLSProvider,
  MediaPlayer,
  MediaProvider,
  useMediaState,
} from "@vidstack/react";
import {
  PlyrLayout,
  plyrLayoutIcons,
} from "@vidstack/react/player/layouts/plyr";
import { memo, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import HLS from "hls.js";

import classes from "./styles.module.scss";

import type { IVideoPlayerProps } from "@components/atoms/videoPlayer/types";

function onProviderChange(provider: MediaProviderAdapter | null) {
  if (isHLSProvider(provider)) {
    provider.library = HLS;

    provider.config = {
      maxBufferLength: 60,
      backBufferLength: 30,
      maxBufferSize: 100 * 1024 * 1024,
      maxBufferHole: 0.5,
      lowLatencyMode: false,
      enableWorker: true,
      progressive: true,
      fragLoadingTimeOut: 20_000,
      fragLoadingMaxRetry: 6,
      fragLoadingRetryDelay: 500,
      manifestLoadingTimeOut: 10_000,
      manifestLoadingMaxRetry: 3,
      levelLoadingTimeOut: 10_000,
      liveSyncDuration: 15,
      liveMaxLatencyDuration: 30,
      abrEwmaDefaultEstimate: 3_000_000,
      abrEwmaFastLive: 3,
      abrEwmaSlowLive: 9,
      abrBandWidthFactor: 0.8,
      abrBandWidthUpFactor: 0.7,
      highBufferWatchdogPeriod: 5,
    };
  }
}

export const VideoPlayer = memo(function VideoPlayer({
  src,
  title,
  watermarkText,
}: IVideoPlayerProps) {
  const player = useRef<MediaPlayerInstance>(null);
  const [isRecording, setIsRecording] = useState(false);
  const canPlay = useMediaState("canPlay", player);

  useEffect(() => {
    if (player?.current) player.current.qualities.switch = "next";
  }, []);

  // 1️⃣ Handle Android "audio-focus pause"
  useEffect(() => {
    const handleMuteCommand = () =>
      player.current && (player.current.muted = true);

    window.addEventListener("pause-vidstack-players", handleMuteCommand);
    return () => {
      window.removeEventListener("pause-vidstack-players", handleMuteCommand);
    };
  }, []);

  // 2️⃣ On mount: catch up & subscribe to recording events
  useEffect(() => {
    // catch-up: re-emit stored state
    const stored = localStorage.getItem("isRecording");

    if (stored === "true") {
      window.dispatchEvent(new CustomEvent("recording-started"));
    } else if (stored === "false") {
      window.dispatchEvent(new CustomEvent("recording-stopped"));
    }

    // subscribe to live events
    const onStart = () => {
      player.current?.pause();
      setIsRecording(true);
    };
    const onStop = () => {
      setIsRecording(false);
    };

    window.addEventListener("recording-started", onStart);
    window.addEventListener("recording-stopped", onStop);
    return () => {
      window.removeEventListener("recording-started", onStart);
      window.removeEventListener("recording-stopped", onStop);
    };
  }, []);

  // Prevent manual play when recording
  const handlePlayAttempt = () => {
    if (isRecording) {
      player.current?.pause();
      return false;
    }
  };

  return (
    <MediaPlayer
      ref={player}
      title={title}
      src={src}
      viewType={"video"}
      streamType={"on-demand"}
      logLevel={"warn"}
      crossOrigin
      playsInline
      load={"visible"}
      preload={"metadata"}
      onPlay={handlePlayAttempt}
      preferNativeHLS={false}
      onProviderChange={onProviderChange}
    >
      <MediaProvider />
      <PlyrLayout icons={plyrLayoutIcons} />

      {/* Loading Spinner */}
      {!canPlay && (
        <div className={clsx(classes.loaderContainer)}>
          <l-ring-2
            size={"60"}
            stroke={"6"}
            stroke-length={"0.25"}
            bg-opacity={"0.3"}
            speed={"0.8"}
            color={"white"}
          />
        </div>
      )}

      {/* Watermark */}
      {watermarkText && (
        <>
          <div className={clsx(classes.watermark1)}>{watermarkText}</div>
          <div className={clsx(classes.watermark2)}>{watermarkText}</div>
        </>
      )}

      {/* Recording Overlay */}
      {isRecording && (
        <div className={clsx(classes.recordOverlay)}>
          {"Screen recording in progress — video disabled"}
        </div>
      )}
    </MediaPlayer>
  );
});
